import * as React from 'react';
import { Room, RoomEvent, DataPacket_Kind } from 'livekit-client';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Divider, TextField, IconButton } from "@mui/material";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import SendIcon from "@mui/icons-material/Send";

interface ChatMessage {
    id: string;
    message: string;
    timestamp: number;
    from: {
        identity: string;
        name?: string;
    };
    isLocal: boolean;
}

interface CustomChatProps {
    room: Room | null;
}

const CHAT_TOPIC = 'lk-chat-topic';

const titleTextStyle = {
    color: 'var(--app-text-primary)',
    paddingLeft: '8px',
    paddingRight: '8px',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    fontFamily: 'Inter, sans-serif',
    userSelect: 'none',
};

const CustomChat: React.FC<CustomChatProps> = ({ room }) => {
    const [messages, setMessages] = React.useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = React.useState('');
    const [isSending, setIsSending] = React.useState(false);
    const messagesEndRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Scroll to bottom when new messages arrive
    React.useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Set up data received listener
    React.useEffect(() => {
        if (!room) return;

        const handleDataReceived = (
            payload: Uint8Array,
            participant: any,
            kind: DataPacket_Kind,
            topic?: string
        ) => {
            if (topic !== CHAT_TOPIC) return;
            
            try {
                const decoder = new TextDecoder();
                const data = JSON.parse(decoder.decode(payload));
                
                const newMessage: ChatMessage = {
                    id: `${Date.now()}-${Math.random()}`,
                    message: data.message,
                    timestamp: data.timestamp || Date.now(),
                    from: {
                        identity: participant?.identity || 'Unknown',
                        name: participant?.name || participant?.identity || 'Unknown',
                    },
                    isLocal: false,
                };
                
                setMessages(prev => [...prev, newMessage]);
            } catch (e) {
                console.error('Failed to parse chat message:', e);
            }
        };

        room.on(RoomEvent.DataReceived, handleDataReceived);

        return () => {
            room.off(RoomEvent.DataReceived, handleDataReceived);
        };
    }, [room]);

    const sendMessage = async () => {
        if (!room || !inputValue.trim() || isSending) return;

        setIsSending(true);
        try {
            const messageData = {
                message: inputValue.trim(),
                timestamp: Date.now(),
            };

            const encoder = new TextEncoder();
            const data = encoder.encode(JSON.stringify(messageData));

            await room.localParticipant.publishData(data, {
                reliable: true,
                topic: CHAT_TOPIC,
            });

            // Add local message to the list
            const localMessage: ChatMessage = {
                id: `${Date.now()}-${Math.random()}`,
                message: inputValue.trim(),
                timestamp: Date.now(),
                from: {
                    identity: room.localParticipant.identity,
                    name: room.localParticipant.name || room.localParticipant.identity,
                },
                isLocal: true,
            };

            setMessages(prev => [...prev, localMessage]);
            setInputValue('');
            inputRef.current?.focus();
        } catch (e) {
            console.error('Failed to send message:', e);
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const formatTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (!room) {
        return (
            <Box sx={{ 
                height: '100%', 
                width: '100%',
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                p: 2,
                minHeight: 0,
                overflow: 'hidden',
            }}>
                <Typography sx={{ color: 'var(--app-text-muted)', fontSize: '13px' }}>
                    Connecting to chat...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            height: '100%', 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            p: '3px',
            overflow: 'hidden',
            minHeight: 0,
        }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                marginLeft: '8px',
                gap: 1,
                py: 1,
            }}>
                <Box sx={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChatOutlinedIcon sx={{ fontSize: 20, color: 'var(--app-text-primary)' }} />
                </Box>
                <Box sx={titleTextStyle}>Chat</Box>
            </Box>

            <Divider sx={{ mx: 1 }} />

            {/* Messages Area */}
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    px: 1.5,
                    py: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    position: 'relative', // For absolute positioned scroll anchor
                }}
            >
                {messages.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // flex: 1,
                    }}>
                        <Typography sx={{
                            color: 'var(--app-text-subtle)',
                            fontSize: '13px',
                            textAlign: 'center',
                        }}>
                            No messages yet.<br/>Start the conversation!
                        </Typography>
                    </Box>
                ) : (
                    messages.map((msg) => (
                        <Box
                            key={msg.id}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: msg.isLocal ? 'flex-end' : 'flex-start',
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: msg.isLocal ? '#2B6BFD' : 'var(--app-bg-subtle)',
                                    color: msg.isLocal ? '#FFFFFF' : 'var(--app-text-primary)',
                                    borderRadius: '12px',
                                    px: 1.5,
                                    py: 1,
                                    maxWidth: '85%',
                                }}
                            >
                                {!msg.isLocal && (
                                    <Typography sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: msg.isLocal ? 'rgba(255,255,255,0.78)' : 'var(--app-text-muted)',
                                        mb: 0.25,
                                    }}>
                                        {msg.from.name}
                                    </Typography>
                                )}
                                <Typography sx={{
                                    fontSize: '13px',
                                    lineHeight: 1.4,
                                    wordBreak: 'break-word',
                                }}>
                                    {msg.message}
                                </Typography>
                                <Typography sx={{
                                    fontSize: '10px',
                                    color: msg.isLocal ? 'rgba(255,255,255,0.72)' : 'var(--app-text-subtle)',
                                    mt: 0.25,
                                    textAlign: 'right',
                                }}>
                                    {formatTime(msg.timestamp)}
                                </Typography>
                            </Box>
                        </Box>
                    ))
                )}
                {/*<Box ref={messagesEndRef} style={{ position: 'absolute', bottom: 0, height: 1, width: 1, pointerEvents: 'none' }} />*/}
            </Box>

            {/* Input Area */}
            <Box
                sx={{
                    borderTop: '1px solid var(--app-border-subtle)',
                    p: 1.5,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                }}
            >
                <TextField
                    inputRef={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    disabled={isSending}
                    size="small"
                    fullWidth
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: 'var(--app-text-primary)',
                            backgroundColor: 'var(--app-bg-subtle)',
                            '& fieldset': {
                                borderColor: 'var(--app-border-subtle)',
                            },
                            '&:hover fieldset': {
                                borderColor: 'var(--app-border-muted)',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#3B82F6',
                            },
                        },
                        '& .MuiInputBase-input': {
                            py: 1,
                        },
                        '& .MuiInputBase-input::placeholder': {
                            color: 'var(--app-text-subtle)',
                            opacity: 1,
                        },
                    }}
                />
                <IconButton
                    onClick={sendMessage}
                    disabled={isSending || !inputValue.trim()}
                    sx={{
                        backgroundColor: '#2B6BFD',
                        color: '#FFFFFF',
                        width: 36,
                        height: 36,
                        transition: 'background-color 0.12s ease',
                        '&:hover': {
                            backgroundColor: '#245DDB',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: 'var(--app-border-subtle)',
                            color: 'var(--app-text-subtle)',
                        },
                    }}
                >
                    <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>
        </Box>
    );
};

export default CustomChat;
